import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import SettingsNavigation, { normalizeSettingsSearch, settingsGroups, type SettingsGroup } from "@/components/settings/SettingsNavigation";
import { saveProfilePreference, type ProfilePreference } from "@/lib/settingsPreferences";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import "@/components/settings/settings.css";
import MobileLayout from "@/components/MobileLayout";
import { APP_VERSION } from "@/lib/appVersion";
import { supabase } from "@/integrations/supabase/client";
import { useAudioAutoplay } from "@/hooks/useAudioAutoplay";
import QuotaMeter from "@/components/QuotaMeter";
import { useCredits } from "@/hooks/useCredits";
import IosCreditSheet from "@/components/paywall/IosCreditSheet";
import { unitsToCredits, unitsToTextMessages, unitsToVoiceMinutes } from "@/config/credits";
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  Lock,
  Trash2,
  Download,
  LogOut,
  FileText,
  BookOpen,
  XCircle,
  LifeBuoy,
  Gift,
  Bell,
} from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import vyvIcon from "@/assets/vyv-icon.png";
import { useLanguage } from "@/i18n/LanguageProvider";
import { languageDef, localeTagFor } from "@/i18n/locale";
import LanguageSelector from "@/components/LanguageSelector";
import SettingsCollapsibleCard from "@/components/settings/SettingsCollapsibleCard";
import SummariesOptOutRow, { summariesOptOutFromProfile } from "@/components/settings/SummariesOptOutRow";
import type { Lang } from "@/i18n/translations";
import { isSfxEnabled, setSfxEnabled } from "@/lib/sfx";
import SleepActionModal, { SleepPendingActionLauncher } from '@/components/mirror/SleepActionModal';
import { invokeSleepAction, newSleepOperationId, snapshotForSleepPreferences, type SleepActionPrefill, type SleepPreferencesSnapshot, type SleepResponse } from '@/lib/sleepExecutor';
import { Sentry } from "@/lib/sentry";
import { useAvatarSnapshot } from "@/hooks/useAvatarSnapshot";
import { useOneSignal } from "@/hooks/useOneSignal";
import { invalidateAvatarCache } from "@/hooks/useAvatar";
import { isPaymentUiBlocked, isNativeIapEnabled } from "@/lib/platform";

// ── Constants ─────────────────────────────────────────────────────────────────

// Mirror suggestion chips toggle. Same localStorage key MirrorChat reads as its
// offline/pre-migration fallback (the DB column show_mirror_suggestions is the
// source of truth once the migration lands).
const MIRROR_SUGGESTIONS_KEY = "vyv_mirror_suggestions_on";

// ── Sub-components ─────────────────────────────────────────────────────────────

export const SectionLabel = ({ label }: { label: string }) => (
  <h2 className="text-sm font-display font-semibold text-foreground mb-3 px-1">
    {label}
  </h2>
);

export const Toggle = ({ on, onToggle, label, disabled = false }: { on: boolean; onToggle: () => void; label?: string; disabled?: boolean }) => (
  <button type="button" onClick={onToggle} aria-pressed={on} aria-label={label} disabled={disabled}
    className="relative w-12 h-11 shrink-0 rounded-xl focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50">
    <span aria-hidden="true" className={`absolute inset-x-0 top-2.5 h-6 rounded-full transition-colors ${on ? "bg-primary" : "bg-foreground/20"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${on ? "left-0.5 translate-x-6" : "left-0.5"}`} />
    </span>
  </button>
);

export const NavRow = ({
  icon,
  label,
  sublabel,
  onClick,
  destructive = false,
  noChevron = false,
}: {
  icon?: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick: () => void;
  destructive?: boolean;
  noChevron?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-3.5 px-4 py-3.5 transition-colors text-left ${
      destructive
        ? "hover:bg-red-500/[0.04] active:bg-red-500/[0.07]"
        : "hover:bg-foreground/[0.04] active:bg-foreground/[0.07]"
    }`}
  >
    {icon && (
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          destructive ? "bg-red-400/[0.08]" : "bg-foreground/[0.06]"
        }`}
      >
        {icon}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p
        className={`font-display text-sm font-medium ${
          destructive ? "text-red-400/80" : "text-foreground/85"
        }`}
      >
        {label}
      </p>
      {sublabel && (
        <p className="text-muted-foreground text-[13px] mt-0.5 leading-relaxed">{sublabel}</p>
      )}
    </div>
    {!destructive && !noChevron && (
      <ChevronRight size={14} className="text-foreground/25 shrink-0" />
    )}
  </button>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const Settings = () => {
  const navigate = useNavigate();
  const { t, tf, tp, lang } = useLanguage();
  const { snapshotUrl, hasAvatar } = useAvatarSnapshot();

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedGroup = searchParams.get("section");
  const group: SettingsGroup = settingsGroups.includes(requestedGroup as SettingsGroup) ? requestedGroup as SettingsGroup : "personal";
  const [query, setQuery] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [loadVersion, setLoadVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const accountRef = useRef<string | null>(null);
  const accountEpoch = useRef(0);
  const renderAccountEpoch = accountEpoch.current;
  useEffect(() => { const lifecycle = accountEpoch; return () => { lifecycle.current++; }; }, []);
  const sectionGroups: Record<string, SettingsGroup> = {
    guide: "help", profile: "personal", connections: "routine", avatar: "personal", voice: "personal",
    usage: "account", credits: "account", referral: "account", personalization: "personal",
    notifications: "routine", audio: "personal", myData: "account", privacy: "account", account: "account", support: "help",
  };
  const keywords: Record<string, string[]> = {
    guide: ["settings.aboutVyv.title", "settings.aboutVyv.subtitle"],
    profile: ["settings.section.profile", "settings.editProfile"],
    connections: ["settings.section.connections", "settings.connections.manage", "settings.ux.connectionsHint"],
    avatar: ["settings.section.avatar", "settings.avatar.manage"],
    voice: ["settings.section.voice", "settings.voice.manage", "settings.voice.autoplay"],
    usage: ["settings.ux.usage"], credits: ["credits.title", "credits.buyMore"], referral: ["settings.section.referral"],
    personalization: ["settings.section.personalization", "settings.mirrorTone", "settings.mirrorLanguage.title", "settings.mirrorSuggestions.title", "settings.firm", "settings.balanced", "settings.warm"],
    notifications: ["settings.section.notifications", "settings.notifications.dailyBrief", "settings.notifications.alerts", "settings.notifications.wakeup", "settings.reminders.entry"],
    audio: ["settings.section.audio", "settings.audio.sfx.label"],
    myData: ["settings.section.myData", "settings.myData.teachMirror", "settings.myData.checkInHistory"],
    privacy: ["settings.section.privacy", "settings.privacy.summariesOptOut.title", "settings.deleteAccount.title", "settings.ux.privacyKeywords"],
    account: ["settings.section.account", "settings.ux.accountKeywords"], support: ["settings.support.title", "settings.support.subtitle"],
  };
  const isVisible = (id: string) => query.trim()
    ? normalizeSettingsSearch((keywords[id] || []).map(t).join(" ")).includes(normalizeSettingsSearch(query))
    : sectionGroups[id] === group;

  const [profile, setProfile] = useState<any>(null);
  // No spinner without a ceiling (12/06 rule): if the profile fetch hangs, the
  // pulsing avatar skeleton below would pulse forever. After 6s fall through to
  // the static avatar block (it renders the "U" initial while profile is still
  // null) instead of an infinite pulse.
  const [profileLoadTimedOut, setProfileLoadTimedOut] = useState(false);
  useEffect(() => {
    if (profile) return;
    const tm = window.setTimeout(() => setProfileLoadTimedOut(true), 6000);
    return () => window.clearTimeout(tm);
  }, [profile]);
  const [email, setEmail] = useState("");
  const [selectedTone, setSelectedTone] = useState("balanced");
  const [suggestionsOn, setSuggestionsOn] = useState(true);
  const [notifDailyBrief, setNotifDailyBrief] = useState(true);
  const [notifAlerts, setNotifAlerts] = useState(true);
  // Wake-up push (sleep-wakeup-message edge fn). Persisted in
  // user_preferences (the table the cron function reads) — NOT user_profiles
  // like the toggles above. DB-first hydration in load(); the
  // vyv_sleep_wakeup_* localStorage keys are only an offline/cache fallback
  // shared with the Sleep settings sheet.
  const [wakeupOn, setWakeupOn] = useState(false);
  const [wakeupTime, setWakeupTime] = useState("07:00");
  // Hydration flags — the notification toggles render a skeleton until their
  // REAL state arrived from the DB. Before this, a failed/slow load left the
  // hardcoded useState defaults on screen (prod 11/06: DB false, UI ON).
  const [notifLoaded, setNotifLoaded] = useState(false);
  const [wakeupLoaded, setWakeupLoaded] = useState(false);
  const sleepPreferencesOriginal = useRef<SleepPreferencesSnapshot|null|undefined>(undefined);
  const [sleepActionPrefill, setSleepActionPrefill] = useState<SleepActionPrefill|null>(null);
  // Supabase user id — backs the OneSignal hook so enabling a notification
  // toggle can trigger the native push permission prompt for this user.
  const [userId, setUserId] = useState<string | null>(null);
  // VYV sonic identity — single SFX toggle (vyv_sfx_enabled, localStorage).
  // Defaults ON, matching the canonical flag read by levelUpSound.
  const [sfxEnabled, setSfxEnabledState] = useState(() => isSfxEnabled());
  // V4.3 — TTS auto-play opt-in (default OFF; on-demand "listen" always works).
  const [autoplayAudio, setAutoplayAudio] = useAudioAutoplay();
  const { balanceUnits: creditsBalanceUnits, refresh: refreshCredits } = useCredits();
  const [creditSheetOpen, setCreditSheetOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const {
    isPremium,
    isCanceledButActive,
    currentPeriodEnd,
    refresh: refreshSubscription,
  } = useSubscription();

  // Reuse the existing push hook (web + native via @/lib/oneSignal) so a
  // notification toggle can ask for the OS-level permission. isSubscribed is
  // false until permission is granted; on native it stays false at mount (the
  // plugin has no synchronous getter), so the first enable always prompts —
  // OneSignal's requestPermission is idempotent and never re-prompts once the
  // permission is already determined.
  const { isSubscribed: pushGranted, requestPermission: requestPushPermission } =
    useOneSignal(userId);

  const tones = [
    {
      value: "firm",
      label: t("settings.firm"),
      desc: t("settings.firmDesc"),
      preview: t("settings.firmPreview"),
    },
    {
      value: "balanced",
      label: t("settings.balanced"),
      desc: t("settings.balancedDesc"),
      preview: t("settings.balancedPreview"),
    },
    {
      value: "warm",
      label: t("settings.warm"),
      desc: t("settings.warmDesc"),
      preview: t("settings.warmPreview"),
    },
  ];

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12000);
    accountRef.current = null; sleepPreferencesOriginal.current=undefined; setSleepActionPrefill(null);
    setProfile(null); setEmail(""); setNotifLoaded(false); setWakeupLoaded(false); setLoadError(false);
    const load = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) throw authError || new Error("No session");
        if (!active) return;
        accountRef.current = user.id;
        setUserId(user.id); setEmail(user.email || "");
        const [profileResult, prefsResult] = await Promise.all([
          supabase.from("user_profiles").select("*").eq("user_id", user.id).abortSignal(controller.signal).maybeSingle(),
          invokeSleepAction({action:'get_preferences'},user.id,()=>{if(!active||controller.signal.aborted||accountRef.current!==user.id)throw new Error('invalid_session');}),
        ]);
        if (!active || accountRef.current !== user.id) return;
        if (profileResult.error || !profileResult.data) throw profileResult.error || new Error("No profile");
        const data = profileResult.data;
        setProfile(data);
        setSelectedTone(data.mirror_tone || "balanced");
        setSuggestionsOn(data.show_mirror_suggestions ?? true);
        setNotifDailyBrief(data.notifications_daily_brief ?? true);
        setNotifAlerts(data.notifications_alerts ?? true);
        setNotifLoaded(true);
        sleepPreferencesOriginal.current=snapshotForSleepPreferences(prefsResult.preferences,user.id);
        setWakeupOn(prefsResult.preferences?.sleep_wakeup_reminder ?? false);
        setWakeupTime(prefsResult.preferences?.sleep_wakeup_reminder_time || "07:00");
        setWakeupLoaded(true);
      } catch (error) {
        if (active) {
          setLoadError(true); setProfileLoadTimedOut(true);
          console.warn("[Settings] preferences load failed", error);
        }
      } finally { window.clearTimeout(timer); }
    };
    void load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (accountRef.current && session?.user.id !== accountRef.current) {
        accountEpoch.current++; accountRef.current = null;
        setUserId(null); setProfile(null); setNotifLoaded(false); setWakeupLoaded(false);
        setLoadVersion(version => version + 1);
      }
    });
    return () => { active = false; accountRef.current = null; controller.abort(); window.clearTimeout(timer); subscription.unsubscribe(); };
  }, [loadVersion]);

  const persist = async (write: (id: string) => Promise<void>, confirmed: () => void) => {
    if (savingRef.current || !userId || accountRef.current !== userId) return false;
    const id = userId;
    savingRef.current = true; setSaving(true);
    try {
      await write(id);
      if (accountRef.current !== id) return false;
      confirmed(); toast.success(t("settings.ux.saved")); return true;
    } catch (error) {
      if (accountRef.current === id) toast.error(t("settings.saveError"));
      console.warn("[Settings] preference save failed", error);
      try { Sentry.captureException(error, { tags: { feature: "settings_preference_save" } }); } catch { /* optional telemetry */ }
      return false;
    } finally { savingRef.current = false; setSaving(false); }
  };

  const updateTone = (tone: string) => {
    if (!profile || tone === selectedTone) return;
    void persist(id => saveProfilePreference(id, "mirror_tone", tone), () => setSelectedTone(tone));
  };

  const toggleSuggestions = () => {
    if (!profile) return;
    const next = !suggestionsOn;
    void persist(id => saveProfilePreference(id, "show_mirror_suggestions", next), () => {
      setSuggestionsOn(next);
      try { localStorage.setItem(MIRROR_SUGGESTIONS_KEY, String(next)); } catch { /* cache only */ }
    });
  };

  const toggleNotif = async (next: boolean, setter: (value: boolean) => void, key: ProfilePreference) => {
    if (!notifLoaded) return;
    const saved = await persist(id => saveProfilePreference(id, key, next), () => setter(next));
    if (saved && next && !pushGranted) void requestPushPermission().catch(() => {});
  };

  const saveWakeupPref = async (on:boolean,time:string) => {
    if(!wakeupLoaded||savingRef.current||!userId||accountRef.current!==userId||!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)||sleepPreferencesOriginal.current===undefined)return;
    const operation_id=newSleepOperationId();setSleepActionPrefill({action:'save_preferences',action_id:operation_id,operation_id,expected_user_id:userId,preferences_patch:{sleep_wakeup_reminder:on,sleep_wakeup_reminder_time:time},expected_preferences_snapshot:structuredClone(sleepPreferencesOriginal.current)});
  };
  const onSleepPreferencesConfirmed = async (response:SleepResponse) => {
    if(!userId||accountEpoch.current!==renderAccountEpoch||accountRef.current!==userId||response.action!=='save_preferences'||response.preferences?.user_id!==userId)return;
    sleepPreferencesOriginal.current=snapshotForSleepPreferences(response.preferences,userId);
    setWakeupOn(response.preferences.sleep_wakeup_reminder===true);setWakeupTime(response.preferences.sleep_wakeup_reminder_time||'07:00');
    toast.success(t('settings.ux.saved'));
    if(response.preferences.sleep_wakeup_reminder&&!pushGranted){const expectedOwner=userId,generation=accountEpoch.current;void requestPushPermission({ownerId:expectedOwner,isCurrent:()=>accountRef.current===expectedOwner&&accountEpoch.current===generation}).catch(()=>{});}
  };

  const toggleSfx = (val: boolean) => {
    // Single source of truth: the vyv_sfx_enabled localStorage flag, shared
    // with the Brain level-up sound. Synchronous, device-local (no DB row).
    setSfxEnabled(val);
    setSfxEnabledState(val);
  };

  const handleLogout = async () => {
    // Belt-and-suspenders: clear the module-level avatar cache before signing
    // out so the next login re-hydrates fresh (the onAuthStateChange handler
    // also invalidates, but this guards the immediate teardown path).
    invalidateAvatarCache();
    const { error } = await supabase.auth.signOut();
    if (error) { toast.error(t("settings.saveError")); return; }
    navigate("/");
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("asaas-cancel-subscription");
      if (error) {
        const ctx = (error as Record<string, unknown>).context;
        const status = ctx && typeof ctx === "object" ? (ctx as { status?: number }).status : undefined;
        if (status === 404) {
          toast.error(t("settings.account.cancel.error.noSubscription"));
        } else {
          toast.error(t("settings.account.cancel.error.generic"));
        }
        return;
      }
      const action = (data as { action?: string } | null)?.action;
      if (action === "already_canceled") {
        toast.success(t("settings.account.cancel.alreadyCanceled"));
      } else {
        toast.success(t("settings.account.cancel.success"));
      }
      refreshSubscription();
      setShowCancelConfirm(false);
    } catch (err) {
      console.error("[Settings] cancel threw", err);
      toast.error(t("settings.account.cancel.error.generic"));
    } finally {
      setCancelLoading(false);
    }
  };

  // V4.3 / Apple 5.1.1(v) — real in-app account deletion. Replaces the old
  // partial client-side wipe (which left auth.users + ~55 tables behind). The
  // delete-account edge fn cancels Asaas, cleans external assets, then
  // auth.admin.deleteUser → DB cascade wipes everything.
  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account");
      if (error) {
        console.error("[settings] delete-account failed", error);
        toast.error(t("settings.deleteAccount.error"));
        setDeleteLoading(false);
        return; // do NOT sign out on failure
      }
      // Success — the account is gone; sign out locally and leave.
      await supabase.auth.signOut();
      navigate("/");
    } catch (e) {
      console.error("[settings] delete-account threw", e);
      toast.error(t("settings.deleteAccount.error"));
      setDeleteLoading(false);
    }
  };

  const goalKey = profile?.primary_goal ? `goal.${profile.primary_goal}` : "";

  const toneSummary = profile ? t("settings." + selectedTone) : t("common.loading");
  const languageSummary = languageDef(lang).nativeLabel;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <MotionConfig reducedMotion="user"><MobileLayout noPadding className="settings-page pb-28 relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[320px] h-[160px] rounded-full opacity-[0.04] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse, hsl(187 72% 50%), transparent 70%)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10"
      >
        {/* Back button */}
        <div className="px-5 pt-6 pb-2">
          <MirrorBackButton />
        </div>

        {/* Page header */}
        <div className="px-5 pb-3">
          <p className="vyv-label mb-1.5">{t("settings.preferences")}</p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-tight">
            {t("settings.title")}
          </h1>
        </div>

        <SettingsNavigation group={group} query={query} onQuery={setQuery} onGroup={next => {
          setQuery(""); setSearchParams(previous => { previous.set("section", next); return previous; }, { replace: true });
        }} />
        <div className="px-5 pb-4" aria-live="polite">
          {saving && <p role="status" className="text-sm text-primary">{t("settings.ux.saving")}</p>}
          {loadError && <div role="alert" className="rounded-xl border border-amber-400/40 p-4 text-sm">
            <p>{t("settings.ux.loadError")}</p>
            <button type="button" onClick={() => setLoadVersion(version => version + 1)} className="mt-2 min-h-11 font-semibold text-primary">{t("common.retry")}</button>
          </div>}
          {!Object.keys(sectionGroups).some(isVisible) && <p role="status" className="py-6 text-muted-foreground">{t("settings.ux.noResults")}</p>}
        </div>
        <div className="space-y-6 px-5">

          {/* ══════════════════════════════════════════
              SECTION — About VYV (entry to /guide)
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("guide")} data-settings-section="guide"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04, duration: 0.5 }}
          >
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <NavRow
                icon={<BookOpen size={16} className="text-foreground/70" />}
                label={t("settings.aboutVyv.title")}
                sublabel={t("settings.aboutVyv.subtitle")}
                onClick={() => navigate("/guide")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION 1 — Profile & Avatar
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("profile")} data-settings-section="profile"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.profile")} />
            <div className="vyv-card overflow-hidden">
              {/* Avatar hero */}
              <div className="flex flex-col items-center py-5 px-5 border-b border-foreground/[0.06]">
                {!profile && !profileLoadTimedOut ? (
                  <>
                    <div className="w-[72px] h-[72px] rounded-full bg-foreground/[0.08] mb-4 animate-pulse" />
                    <div className="w-28 h-4 rounded-lg bg-foreground/[0.08] mb-2 animate-pulse" />
                    <div className="w-20 h-3 rounded-lg bg-foreground/[0.05] animate-pulse" />
                  </>
                ) : (
                  <>
                <div
                  className="w-[72px] h-[72px] rounded-full flex items-center justify-center mb-4 relative overflow-hidden"
                  style={{
                    background:
                      "radial-gradient(circle at 40% 35%, hsl(187 72% 18%), hsl(222 38% 10%))",
                    boxShadow:
                      "0 0 0 1px hsl(187 72% 50% / 0.2), 0 0 32px hsl(187 72% 50% / 0.20), 0 0 72px hsl(187 72% 50% / 0.08)",
                  }}
                >
                  {hasAvatar && snapshotUrl ? (
                    <img
                      src={snapshotUrl}
                      alt={profile?.first_name || "Avatar"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="font-display text-[28px] font-semibold text-primary/90 select-none">
                      {(profile?.first_name || "U").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      boxShadow: "inset 0 1px 0 hsl(187 72% 50% / 0.18)",
                    }}
                  />
                </div>

                <p className="font-display text-[17px] font-semibold text-foreground">
                  {profile?.first_name || t("common.user")}
                </p>
                {goalKey && (
                  <p className="text-muted-foreground text-[12px] mt-1 font-body">
                    {t(goalKey)}
                  </p>
                )}
                  </>
                )}
              </div>

              <NavRow
                label={t("settings.editProfile")}
                onClick={() => navigate("/settings/profile")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Integrations
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("connections")} data-settings-section="connections"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.connections")} />
            <div className="vyv-card overflow-hidden">
              <NavRow
                label={t("settings.connections.manage")}
                sublabel={t("settings.ux.connectionsHint")}
                onClick={() => navigate("/settings/connections")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Avatar
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("avatar")} data-settings-section="avatar"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.avatar")} />
            <div className="vyv-card overflow-hidden">
              <NavRow
                label={t("settings.avatar.manage")}
                onClick={() => navigate("/settings/avatars")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Voice (Pedido B, 14/05/26)
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("voice")} data-settings-section="voice"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.voice")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <NavRow
                label={t("settings.voice.manage")}
                onClick={() => navigate("/settings/voice")}
              />

              <div className="flex items-center justify-between px-4 py-3.5">
                <div className="pr-3">
                  <p className="font-display text-sm font-medium text-foreground/85">
                    {t("settings.voice.autoplay")}
                  </p>
                  <p className="font-body text-[13px] text-foreground/50 mt-0.5">
                    {t("settings.voice.autoplayDesc")}
                  </p>
                </div>
                <Toggle label={t("settings.voice.autoplay")} on={autoplayAudio} onToggle={() => setAutoplayAudio(!autoplayAudio)} />
              </div>
            </div>
          </motion.section>

          {/* ── Usage / quota meter (V4.3) ── */}
          <motion.section hidden={!isVisible("usage")} data-settings-section="usage"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.09, duration: 0.5 }}
          >
            <SectionLabel label={t("quota.meter.title")} />
            <QuotaMeter />
          </motion.section>

          {/* ── Credits (top-up pool) ── */}
          <motion.section hidden={!isVisible("credits")} data-settings-section="credits"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.095, duration: 0.5 }}
          >
            <SectionLabel label={t("credits.title")} />
            <div className="vyv-card overflow-hidden">
              {(() => {
                const credits = unitsToCredits(creditsBalanceUnits);
                const mins = unitsToVoiceMinutes(creditsBalanceUnits);
                const msgs = unitsToTextMessages(creditsBalanceUnits);
                const summary = creditsBalanceUnits > 0
                  ? tf("credits.balance.summary", { credits: String(credits), min: String(mins), msgs: String(msgs) })
                  : t("credits.balance.empty");
                // iOS with StoreKit IAP enabled: tappable row → in-app credit
                // sheet (consumable, no entitlement → Free can buy). Apple 3.1.1:
                // NO Asaas/PIX/aivyv.com on iOS — StoreKit only.
                if (isNativeIapEnabled()) {
                  return (
                    <button
                      type="button"
                      onClick={() => setCreditSheetOpen(true)}
                      className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
                    >
                      <div className="flex-1">
                        <p className="font-display text-sm font-medium text-foreground/85">{summary}</p>
                        <p className="font-body text-[13px] text-foreground/50 mt-0.5">{t("credits.buyMore")}</p>
                      </div>
                      <ChevronRight size={14} className="text-foreground/25" />
                    </button>
                  );
                }
                // iOS WITHOUT IAP (flag off): balance only, no purchase path.
                // Web/Android: tappable row → /creditos (Asaas/PIX).
                if (isPaymentUiBlocked()) {
                  return (
                    <div className="px-4 py-3.5">
                      <p className="font-display text-sm font-medium text-foreground/85">{summary}</p>
                      <p className="font-body text-[13px] text-foreground/50 mt-0.5">{t("credits.ios.webHint")}</p>
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    onClick={() => navigate("/creditos")}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
                  >
                    <div className="flex-1">
                      <p className="font-display text-sm font-medium text-foreground/85">{summary}</p>
                      <p className="font-body text-[13px] text-foreground/50 mt-0.5">{t("credits.buyMore")}</p>
                    </div>
                    <ChevronRight size={14} className="text-foreground/25" />
                  </button>
                );
              })()}
            </div>
          </motion.section>

          {/* iOS StoreKit credit sheet — opened from the credits row above
              (native IAP only). onPurchased refreshes the balance shown here. */}
          <IosCreditSheet
            open={creditSheetOpen}
            onClose={() => setCreditSheetOpen(false)}
            onPurchased={refreshCredits}
          />

          {/* ══════════════════════════════════════════
              SECTION — Referral (invite & earn)
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("referral")} data-settings-section="referral"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.098, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.referral")} />
            <div className="vyv-card overflow-hidden">
              <NavRow
                icon={<Gift size={15} className="text-primary" />}
                label={t("referral.settings.label")}
                sublabel={t("referral.settings.sublabel")}
                onClick={() => navigate("/referral")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION 2 — Mirror Personalization
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("personalization")} data-settings-section="personalization"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.personalization")} />
            <div className="space-y-3">

              {/* 2A — Mirror Tone */}
              <SettingsCollapsibleCard forceOpen={!!query.trim()}
                title={t("settings.mirrorTone")}
                summary={toneSummary}
              >
                <div className="space-y-2.5">
                  {tones.map((tn) => (
                    <button
                      key={tn.value}
                      onClick={() => updateTone(tn.value)}
                      disabled={!profile || saving} aria-pressed={selectedTone === tn.value}
                      className={`w-full py-3.5 px-4 rounded-xl font-display text-left transition-all duration-300 flex items-start justify-between gap-3 ${
                        selectedTone === tn.value
                          ? "bg-primary/10 border border-primary/25 shadow-[0_0_16px_hsl(187_72%_50%/0.06)]"
                          : "bg-card/30 border border-border/12 hover:bg-card/45 hover:border-border/20"
                      }`}
                    >
                      <div className="flex-1">
                        <p
                          className={`text-[13px] font-semibold ${
                            selectedTone === tn.value
                              ? "text-primary"
                              : "text-foreground/75"
                          }`}
                        >
                          {tn.label}
                        </p>
                        <p
                          className={`text-xs mt-0.5 ${
                            selectedTone === tn.value
                              ? "text-primary/55"
                              : "text-muted-foreground"
                          }`}
                        >
                          {tn.desc}
                        </p>
                        <p
                          className={`text-[13px] mt-2 italic leading-relaxed ${
                            selectedTone === tn.value
                              ? "text-foreground/55"
                              : "text-muted-foreground/40"
                          }`}
                        >
                          "{tn.preview}"
                        </p>
                      </div>
                      {selectedTone === tn.value && (
                        <div className="w-3 h-3 rounded-full bg-primary/80 mt-1 shrink-0 shadow-[0_0_8px_hsl(187_72%_50%/0.3)]" />
                      )}
                    </button>
                  ))}
                </div>
              </SettingsCollapsibleCard>

              {/* 2B — Mirror Language */}
              <SettingsCollapsibleCard forceOpen={!!query.trim()}
                title={t("settings.mirrorLanguage.title")}
                summary={languageSummary}
              >
                <LanguageSelector
                  variant="grid"
                  ariaLabel={t("settings.mirrorLanguage.title")}
                />
              </SettingsCollapsibleCard>

              {/* 2E — Mirror suggestion chips toggle */}
              <div className="vyv-card p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-foreground/75 text-[13px] font-body leading-snug">
                      {t("settings.mirrorSuggestions.title")}
                    </p>
                    <p className="text-foreground/45 text-[13px] font-body leading-snug mt-1">
                      {t("settings.mirrorSuggestions.subtitle")}
                    </p>
                  </div>
                  <Toggle
                    label={t("settings.mirrorSuggestions.title")} disabled={!profile || saving} on={suggestionsOn}
                    onToggle={toggleSuggestions}
                  />
                </div>
              </div>

            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Notifications
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("notifications")} data-settings-section="notifications"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.13, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.notifications")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <div className="flex items-center justify-between px-4 py-3.5">
                <p className="font-display text-sm font-medium text-foreground/85">
                  {t("settings.notifications.dailyBrief")}
                </p>
                {/* Skeleton until the REAL DB state arrives — a hardcoded
                    default here showed ON while the DB said false (11/06). */}
                {notifLoaded ? (
                  <Toggle label={t("settings.notifications.dailyBrief")} disabled={saving} on={notifDailyBrief} onToggle={() => toggleNotif(!notifDailyBrief, setNotifDailyBrief, "notifications_daily_brief")} />
                ) : (
                  <div className="w-11 h-6 rounded-full bg-foreground/[0.08] animate-pulse shrink-0" />
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-3.5">
                <p className="font-display text-sm font-medium text-foreground/85">
                  {t("settings.notifications.alerts")}
                </p>
                {notifLoaded ? (
                  <Toggle label={t("settings.notifications.alerts")} disabled={saving} on={notifAlerts} onToggle={() => toggleNotif(!notifAlerts, setNotifAlerts, "notifications_alerts")} />
                ) : (
                  <div className="w-11 h-6 rounded-full bg-foreground/[0.08] animate-pulse shrink-0" />
                )}
              </div>
              <div className="px-4 py-3.5">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 pr-3">
                    <p className="font-display text-sm font-medium text-foreground/85">
                      {t("settings.notifications.wakeup")}
                    </p>
                    <p className="text-[13px] text-foreground/40 mt-0.5">
                      {t("settings.notifications.wakeupSub")}
                    </p>
                  </div>
                  {wakeupLoaded ? (
                    <Toggle label={t("settings.notifications.wakeup")} disabled={saving} on={wakeupOn} onToggle={() => saveWakeupPref(!wakeupOn, wakeupTime)} />
                  ) : (
                    <div className="w-11 h-6 rounded-full bg-foreground/[0.08] animate-pulse shrink-0" />
                  )}
                </div>
                {wakeupOn && (
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-[12px] text-foreground/55">
                      {t("settings.notifications.wakeupTime")}
                    </p>
                    <input
                      type="time" aria-label={t("settings.notifications.wakeupTime")} disabled={saving}
                      value={wakeupTime}
                      onChange={(e) => saveWakeupPref(true, e.target.value)}
                      className="rounded-xl px-3 py-1.5 font-display text-[13px] font-semibold text-foreground focus:outline-none bg-foreground/[0.06] border border-foreground/[0.12]"
                      style={{ colorScheme: "dark" }}
                    />
                  </div>
                )}
              </div>
              <NavRow
                icon={<Bell size={15} className="text-foreground/70" />}
                label={t("settings.reminders.entry")}
                sublabel={t("settings.reminders.entrySub")}
                onClick={() => navigate("/settings/reminders")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Audio (VYV sonic identity)
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("audio")} data-settings-section="audio"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.audio")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <div className="flex items-center justify-between px-4 py-3.5 gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-display text-sm font-medium text-foreground/85">
                    {t("settings.audio.sfx.label")}
                  </p>
                  <p className="font-display text-[13px] text-foreground/55 mt-0.5">
                    {t("settings.audio.sfx.description")}
                  </p>
                </div>
                <Toggle
                  label={t("settings.audio.sfx.label")} on={sfxEnabled}
                  onToggle={() => toggleSfx(!sfxEnabled)}
                />
              </div>
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION 3 — My Data
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("myData")} data-settings-section="myData"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.myData")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <NavRow
                label={t("settings.myData.teachMirror")}
                sublabel={t("settings.myData.teachMirrorSubtitle")}
                onClick={() => navigate("/teach-mirror")}
              />
              <NavRow
                label={t("settings.myData.checkInHistory")}
                onClick={() => navigate("/progress")}
              />
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION 5 — Privacy & Security
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("privacy")} data-settings-section="privacy"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.privacy")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              {/* Encryption info */}
              <div className="px-4 py-4 flex items-start gap-3.5">
                <div className="w-8 h-8 rounded-lg bg-primary/[0.08] flex items-center justify-center shrink-0 mt-0.5">
                  <Shield className="text-primary/50" size={14} />
                </div>
                <div className="flex-1">
                  <p className="font-display text-sm font-medium text-foreground/85">
                    {t("settings.encrypted")}
                  </p>
                  <p className="text-muted-foreground text-[13px] leading-relaxed mt-0.5 font-body">
                    {t("settings.encryptedDesc")}
                  </p>
                </div>
              </div>

              {/* LOTE PRIVACIDADE 1 — opt-out de resumo automático (direito de
                  limitar o uso de dado sensível). Lê/escreve
                  user_profiles.summaries_opt_out; componente próprio para ser
                  testável sem montar a página inteira. */}
              <SummariesOptOutRow userId={userId} value={summariesOptOutFromProfile(profile)} />

              {/* Privacy Policy — self-hosted at /privacy (src/pages/Legal/) */}
              <Link
                to="/privacy"
                className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                  <FileText size={14} className="text-foreground/50" />
                </div>
                <p className="font-display text-sm font-medium text-foreground/85 flex-1">
                  {t("settings.privacy.policy")}
                </p>
                <ChevronRight size={14} className="text-foreground/25" />
              </Link>

              {/* Export */}
              <button
                type="button"
                onClick={() => navigate("/privacy-request")}
                className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                  <Download size={14} className="text-foreground/50" />
                </div>
                <p className="font-display text-sm font-medium text-foreground/85 flex-1">
                  {t("ux.data.request")}
                </p>
                <ChevronRight size={14} className="text-foreground/25" />
              </button>

              {/* Danger zone — permanent account deletion (Apple 5.1.1(v)). */}
              <AnimatePresence initial={false}>
                {!showDeleteConfirm ? (
                  <motion.div
                    key="delete-trigger"
                    initial={false}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-red-500/[0.04] active:bg-red-500/[0.07] transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-400/[0.08] flex items-center justify-center shrink-0">
                        <Trash2 size={14} className="text-red-400/70" />
                      </div>
                      <p className="font-display text-sm font-medium text-red-400/80 flex-1">
                        {t("settings.deleteAccount.trigger")}
                      </p>
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="delete-confirm"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 py-4 space-y-3">
                      <p className="text-[13px] text-red-400/90 font-display font-semibold">
                        {t("settings.deleteAccount.title")}
                      </p>
                      <p className="text-[12px] text-foreground/55 font-body leading-relaxed">
                        {t("settings.deleteAccount.warning")}
                      </p>
                      <p className="text-[12px] text-foreground/55 font-body leading-relaxed">
                        {tf("settings.deleteAccount.confirmPrompt", { word: t("settings.deleteAccount.confirmWord") })}
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        disabled={deleteLoading}
                        placeholder={t("settings.deleteAccount.confirmWord")}
                        className="w-full px-3 py-2.5 rounded-xl bg-foreground/[0.05] border border-red-500/20 text-foreground/90 text-[13px] font-display tracking-wide placeholder:text-foreground/30 focus:outline-none focus:border-red-500/40"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleDeleteAccount}
                          disabled={
                            deleteLoading ||
                            deleteConfirmText.trim().toUpperCase() !==
                              t("settings.deleteAccount.confirmWord").toUpperCase()
                          }
                          className="flex-1 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-[12px] font-display font-semibold hover:bg-red-500/[0.16] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {deleteLoading
                            ? t("settings.deleteAccount.deleting")
                            : t("settings.deleteAccount.confirmButton")}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                          disabled={deleteLoading}
                          className="flex-1 py-2.5 rounded-xl bg-foreground/[0.05] border border-foreground/10 text-foreground/60 text-[12px] font-display font-semibold hover:bg-foreground/[0.08] transition-colors disabled:opacity-40"
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION 6 — Account
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("account")} data-settings-section="account"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.5 }}
          >
            <SectionLabel label={t("settings.section.account")} />
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              {/* Current plan row.
                  - Premium user: shows "VYV Premium" + end date (or
                    "ends DD/MM" when already canceled).
                  - Free user: row that opens /pricing (web) or non-
                    interactive label (iOS 3.1.1).
                  Cancellation is account management, not a purchase, so
                  the Cancel button is NOT gated by isPaymentUiBlocked()
                  and the dialog copy never mentions price or URL. */}
              {isPremium ? (
                <div className="w-full flex items-center gap-3.5 px-4 py-3.5 text-left">
                  <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                    <Shield size={14} className="text-primary/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-display tracking-[0.2em] uppercase text-foreground/30">
                      {t("settings.account.plan")}
                    </p>
                    <p className="text-foreground/80 text-[13px] font-body mt-0.5">
                      {t("settings.account.plan.premium")}
                    </p>
                    {currentPeriodEnd && (
                      <p className="text-foreground/40 text-[13px] font-body mt-0.5">
                        {tf(
                          isCanceledButActive
                            ? "settings.account.plan.endsOn"
                            : "settings.account.plan.renewsOn",
                          { date: formatPlanEndDate(currentPeriodEnd, lang) },
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // Free user (ALL platforms incl. iOS): tappable row → /upgrade.
                // /upgrade is itself 3.1.1-safe (static "visit aivyv.com" text
                // on iOS, "See plans" CTA on web/Android), so this row carries
                // NO purchase affordance on iOS — only the neutral pitch entry.
                <button
                  type="button"
                  onClick={() => navigate("/upgrade")}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                    <Shield size={14} className="text-foreground/30" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-display tracking-[0.2em] uppercase text-foreground/30">
                      {t("settings.account.plan")}
                    </p>
                    <p className="text-foreground/70 text-[13px] font-body mt-0.5">VYV Free</p>
                  </div>
                  <span className="text-[13px] font-display text-primary/70">
                    {isPaymentUiBlocked() ? t("settings.account.learnMore") : t("settings.account.seePlans")}
                  </span>
                  <ChevronRight size={14} className="text-foreground/25 shrink-0" />
                </button>
              )}

              {/* Cancel subscription — visible only for Premium users who
                  have NOT already canceled. Safe on iOS (no price/URL). */}
              {isPremium && !isCanceledButActive && (
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(true)}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-foreground/[0.04] active:bg-foreground/[0.07] transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                    <XCircle size={14} className="text-foreground/40" />
                  </div>
                  <p className="font-display text-sm font-medium text-foreground/70 flex-1">
                    {t("settings.account.cancel.button")}
                  </p>
                </button>
              )}

              {/* Email (read-only) */}
              <div className="flex items-center gap-3.5 px-4 py-3.5">
                <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0">
                  <Lock size={14} className="text-foreground/30" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-display tracking-[0.2em] uppercase text-foreground/30">
                    {t("settings.account.email")}
                  </p>
                  <p className="text-foreground/50 text-[13px] font-body truncate mt-0.5">
                    {email}
                  </p>
                </div>
              </div>

              {/* Sign out */}
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3.5 px-4 py-3.5 hover:bg-red-500/[0.04] active:bg-red-500/[0.07] transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-red-400/[0.08] flex items-center justify-center shrink-0">
                  <LogOut size={14} className="text-red-400/70" />
                </div>
                <p className="font-display text-sm font-medium text-red-400/80 flex-1">
                  {t("settings.signOut")}
                </p>
              </button>
            </div>
          </motion.section>

          {/* ══════════════════════════════════════════
              SECTION — Support (moved to page end, 04/06)
          ══════════════════════════════════════════ */}
          <motion.section hidden={!isVisible("support")} data-settings-section="support"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="vyv-card overflow-hidden divide-y divide-foreground/[0.05]">
              <NavRow
                icon={<LifeBuoy size={16} className="text-foreground/70" />}
                label={t("settings.support.title")}
                sublabel={t("settings.support.subtitle")}
                onClick={() => navigate("/support")}
              />
            </div>
          </motion.section>

          <AlertDialog open={showCancelConfirm} onOpenChange={open => { if (!cancelLoading) setShowCancelConfirm(open); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("settings.account.cancel.dialog.title")}</AlertDialogTitle>
                <AlertDialogDescription>{currentPeriodEnd
                  ? tf("settings.account.cancel.dialog.body", { date: formatPlanEndDate(currentPeriodEnd, lang) })
                  : t("settings.account.cancel.dialog.bodyNoDate")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={cancelLoading}>{t("settings.account.cancel.keep")}</AlertDialogCancel>
                <button type="button" onClick={handleCancelSubscription} disabled={cancelLoading} className="min-h-11 rounded-lg px-4 bg-destructive text-destructive-foreground font-medium">
                  {t(cancelLoading ? "common.loading" : "settings.account.cancel.confirm")}
                </button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Footer */}
          <div className="flex flex-col items-center justify-center gap-1.5 py-4">
            <div className="flex items-center justify-center gap-2">
              <img
                src={vyvIcon}
                alt="VYV"
                className="w-4 h-4 object-contain opacity-15"
              />
              <p className="text-muted-foreground/20 text-[9px] font-display tracking-[0.2em]">
                {t("settings.footer")}
              </p>
            </div>
            {/* itSeez3D / MetaPerson EULA attribution — required, discreet. */}
            <a
              href="https://avatarsdk.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground/25 hover:text-muted-foreground/45 text-[9px] font-display tracking-[0.15em] transition-colors"
            >
              {t("common.poweredByAvatarSdk")}
            </a>
            {/* Build stamp (deployed git SHA) — lets QA confirm on-screen which
                bundle a device is actually running (stale-cache triage). */}
            <p className="text-muted-foreground/25 text-[9px] font-display tracking-[0.15em]">
              v{APP_VERSION || "dev"}
            </p>
          </div>

        </div>
      </motion.div>

      {userId&&<SleepPendingActionLauncher ownerId={userId} busy={saving||!!sleepActionPrefill} onMirrorResume={locator=>navigate('/mirror',{state:{sleepActionRecovery:locator}})} onConfirmed={onSleepPreferencesConfirmed} />}
      <SleepActionModal open={!!sleepActionPrefill} prefill={sleepActionPrefill} onClose={()=>setSleepActionPrefill(null)} onCreated={()=>undefined} onConfirmed={onSleepPreferencesConfirmed} />
    </MobileLayout></MotionConfig>
  );
};

function formatPlanEndDate(iso: string, lang: Lang): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const d = new Date(t);
  // Lang values are UPPERCASE ("PT-BR" | "EN" | "NL"). The old lowercase
  // comparison ("pt-BR"/"nl") never matched, so EVERY locale fell through to
  // en-US and Settings rendered mm/dd ("07/11" for July 11th) — prod 11/06.
  const locale = localeTagFor(lang);
  try {
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export default Settings;
